import { useState, useRef, useMemo, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';
import { formatDate } from '../lib/dateUtils';
import { Landmark, Check, X } from 'lucide-react';

const fmt = (n: number) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtD = (iso: string) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}-${m}-${y}`; };

export const BankReconciliationView = ({ handleItemSelect }: any) => {
  const activeCompany = useStore(s => s.activeCompany);
  const fromDate = useStore(s => s.fromDate);
  const toDate = useStore(s => s.toDate);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [reconDates, setReconDates] = useState<Record<number, string>>({});
  const [bankStatementBal, setBankStatementBal] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const scrollRef = useRef<HTMLDivElement>(null);

  const bankLedgers = useLiveQuery(async () => {
    if (!activeCompany) return [];
    const bankGroup = await db.groups.where('companyId').equals(activeCompany.id!).and(g => g.name === 'Bank Accounts').first();
    if (!bankGroup) return [];
    return db.ledgers.where('groupId').equals(bankGroup.id!).filter(l => l.companyId === activeCompany.id!).toArray();
  }, [activeCompany]);

  const transactions = useLiveQuery(async () => {
    if (!activeCompany || !selectedBankId) return [];
    const entries = await db.voucherEntries.where('ledgerId').equals(selectedBankId).toArray();
    const result = [];
    for (const entry of entries) {
      const voucher = await db.vouchers.get(entry.voucherId);
      if (!voucher || voucher.deleted) continue;
      if (voucher.date < fromDate || voucher.date > toDate) continue;
      const otherEntries = await db.voucherEntries.where('voucherId').equals(entry.voucherId).toArray();
      const otherLedgerIds = otherEntries.filter(e => e.ledgerId !== selectedBankId).map(e => e.ledgerId);
      const otherLedgers = await Promise.all(otherLedgerIds.map(id => db.ledgers.get(id)));
      const particulars = otherLedgers.filter(Boolean).map(l => l!.name).join(', ') || voucher.type;
      result.push({
        id: entry.id!,
        voucherId: voucher.id!,
        date: voucher.date,
        type: voucher.type,
        voucherNo: voucher.voucherNumber,
        refNo: voucher.referenceNumber || '',
        narration: voucher.narration || '',
        particulars,
        debit: entry.type === 'Dr' ? entry.amount : 0,
        credit: entry.type === 'Cr' ? entry.amount : 0,
      });
    }
    return result.sort((a, b) => a.date.localeCompare(b.date));
  }, [activeCompany, selectedBankId, fromDate, toDate]);

  const selectedLedger = bankLedgers?.find(l => l.id === selectedBankId);
  const openingBal = selectedLedger ? (selectedLedger.balanceType === 'Dr' ? selectedLedger.openingBalance : -selectedLedger.openingBalance) : 0;

  const { reconciledItems, unreconciledItems, reconciledBal, unreconciledBal, bookBalance } = useMemo(() => {
    if (!transactions) return { reconciledItems: [], unreconciledItems: [], reconciledBal: 0, unreconciledBal: 0, bookBalance: 0 };
    const reconciled = transactions.filter(t => reconDates[t.id]);
    const unreconciled = transactions.filter(t => !reconDates[t.id]);
    const rBal = reconciled.reduce((s, t) => s + t.debit - t.credit, 0);
    const uBal = unreconciled.reduce((s, t) => s + t.debit - t.credit, 0);
    const total = transactions.reduce((s, t) => s + t.debit - t.credit, 0);
    return { reconciledItems: reconciled, unreconciledItems: unreconciled, reconciledBal: rBal, unreconciledBal: uBal, bookBalance: openingBal + total };
  }, [transactions, reconDates, openingBal]);

  const bankBal = parseFloat(bankStatementBal.replace(/,/g, '')) || 0;
  const difference = bookBalance - bankBal;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleItemSelect({ name: 'Quit' });
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const toggleRecon = (id: number) => {
    setReconDates(prev => {
      const next = { ...prev };
      if (next[id]) { delete next[id]; }
      else { next[id] = new Date().toISOString().split('T')[0]; }
      return next;
    });
  };

  if (!selectedBankId) {
    return (
      <div className="flex flex-col w-full h-full bg-white absolute inset-0 font-sans text-[12px] text-black">
        <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
          <span className="w-1/3 text-left">Bank Reconciliation</span>
          <span className="w-1/3 text-center">{activeCompany?.name}</span>
          <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
        </div>
        <div className="bg-white text-black flex justify-between px-2 py-1 font-bold border-b border-[#aebec8] z-10 shadow-sm">
          <span>Select Bank Account</span>
          <span>{fmtD(fromDate)} to {fmtD(toDate)}</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {(!bankLedgers || bankLedgers.length === 0) ? (
            <div className="text-center text-gray-400 py-16">
              <Landmark className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <div className="text-[13px]">No bank accounts found</div>
              <div className="text-[11px] text-gray-300 mt-1">Create a ledger under "Bank Accounts" group first</div>
            </div>
          ) : (
            <div className="max-w-[400px] mx-auto space-y-2">
              <div className="text-[13px] font-bold text-[#1e8585] mb-3">Choose a bank account to reconcile:</div>
              {bankLedgers.map(bank => (
                <div key={bank.id} onClick={() => setSelectedBankId(bank.id!)}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-[#f0f9f9] hover:border-[#1e8585] transition-colors">
                  <Landmark className="w-5 h-5 text-[#1e8585]" />
                  <div>
                    <div className="text-[13px] font-semibold">{bank.name}</div>
                    <div className="text-[10px] text-gray-400">Opening: ₹{fmt(bank.openingBalance)} {bank.balanceType}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white absolute inset-0 font-sans text-[12px] text-black select-none">
      {/* Header */}
      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm z-10">
        <span className="w-1/3 text-left">Bank Reconciliation</span>
        <span className="w-1/3 text-center">{activeCompany?.name}</span>
        <span className="w-1/3 text-right cursor-pointer hover:text-gray-300" onClick={() => handleItemSelect({ name: 'Quit' })}>✕</span>
      </div>

      {/* Bank info bar */}
      <div className="bg-white text-black flex items-center justify-between px-2 py-1 font-bold border-b border-[#aebec8] shadow-sm shrink-0">
        <div className="flex items-center gap-3">
          <Landmark className="w-4 h-4 text-[#1e8585]" />
          <span className="text-[13px] text-[#1e8585]">{selectedLedger?.name}</span>
          <span className="text-[10px] text-gray-400">{fmtD(fromDate)} to {fmtD(toDate)}</span>
        </div>
        <button onClick={() => setSelectedBankId(null)} className="text-[10px] text-gray-500 hover:text-[#1e8585] border border-gray-200 px-2 py-[2px] rounded">
          Change Bank
        </button>
      </div>

      {/* Summary strip */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] border-b border-gray-200 shrink-0 gap-4">
        <div className="flex items-center gap-4">
          <div className="text-center">
            <div className="text-[9px] text-gray-500 font-semibold uppercase">Book Balance</div>
            <div className="text-[14px] font-bold text-gray-800">₹{fmt(bookBalance)}</div>
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <div className="text-center">
            <div className="text-[9px] text-gray-500 font-semibold uppercase">Reconciled</div>
            <div className="text-[14px] font-bold text-emerald-600">{Object.keys(reconDates).length} / {transactions?.length || 0}</div>
          </div>
          <div className="h-6 w-px bg-gray-300" />
          <div className="text-center">
            <div className="text-[9px] text-gray-500 font-semibold uppercase">Unreconciled Amt</div>
            <div className={`text-[14px] font-bold ${unreconciledBal !== 0 ? 'text-amber-600' : 'text-gray-400'}`}>₹{fmt(Math.abs(unreconciledBal))}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-gray-500">Bank Statement Bal:</span>
            <span className="text-gray-500">₹</span>
            <input type="text" value={bankStatementBal} onChange={e => setBankStatementBal(e.target.value)}
              placeholder="0.00" className="w-[100px] outline-none border border-gray-300 rounded px-2 py-[2px] text-right text-[12px] font-bold focus:border-[#1e8585]" />
          </div>
          {bankBal > 0 && (
            <div className={`text-[11px] font-bold px-2 py-[2px] rounded ${Math.abs(difference) < 0.01 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'}`}>
              {Math.abs(difference) < 0.01 ? '✓ Matched' : `Diff: ₹${fmt(Math.abs(difference))}`}
            </div>
          )}
        </div>
      </div>

      {/* Column headers */}
      <div className="flex w-full border-b border-[#aebec8] bg-white text-black font-bold px-2 py-1 shadow-sm shrink-0">
        <div className="w-[30px] text-center">✓</div>
        <div className="w-[80px]">Date</div>
        <div className="w-[70px]">Type</div>
        <div className="w-[70px]">Vch No.</div>
        <div className="flex-1">Particulars</div>
        <div className="w-[110px] text-right">Debit</div>
        <div className="w-[110px] text-right">Credit</div>
        <div className="w-[90px] text-center">Recon Date</div>
      </div>

      {/* Transaction list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-white scrollbar-thin pb-6">
        {(!transactions || transactions.length === 0) ? (
          <div className="text-center text-gray-400 py-16">
            <div className="text-[13px]">No bank transactions in this period</div>
          </div>
        ) : (
          transactions.map((t, idx) => {
            const isRecon = !!reconDates[t.id];
            return (
              <div key={t.id}
                className={`flex w-full px-2 py-[3px] cursor-pointer border-b border-gray-50 transition-colors ${
                  selectedIdx === idx ? 'bg-[#1e8585] text-white font-bold' : isRecon ? 'bg-emerald-50/50 text-black' : 'text-black hover:bg-gray-50'
                }`}
                onClick={() => toggleRecon(t.id)}
                onMouseEnter={() => setSelectedIdx(idx)}
              >
                <div className="w-[30px] flex items-center justify-center">
                  {isRecon ? (
                    <Check className={`w-3.5 h-3.5 ${selectedIdx === idx ? 'text-white' : 'text-emerald-600'}`} />
                  ) : (
                    <div className={`w-3 h-3 rounded border ${selectedIdx === idx ? 'border-white/50' : 'border-gray-300'}`} />
                  )}
                </div>
                <div className={`w-[80px] ${selectedIdx === idx ? 'text-white/80' : 'text-gray-500'}`}>{fmtD(t.date)}</div>
                <div className={`w-[70px] ${selectedIdx === idx ? 'text-white/80' : 'text-gray-500'}`}>{t.type}</div>
                <div className={`w-[70px] ${selectedIdx === idx ? 'text-white/70' : 'text-gray-400'}`}>#{t.voucherNo}</div>
                <div className="flex-1 truncate">{t.particulars}</div>
                <div className={`w-[110px] text-right font-semibold ${t.debit > 0 ? (selectedIdx === idx ? 'text-white' : 'text-gray-800') : ''}`}>
                  {t.debit > 0 ? `₹${fmt(t.debit)}` : ''}
                </div>
                <div className={`w-[110px] text-right font-semibold ${t.credit > 0 ? (selectedIdx === idx ? 'text-white' : 'text-gray-800') : ''}`}>
                  {t.credit > 0 ? `₹${fmt(t.credit)}` : ''}
                </div>
                <div className={`w-[90px] text-center text-[10px] ${selectedIdx === idx ? 'text-white/70' : 'text-gray-400'}`}>
                  {isRecon ? fmtD(reconDates[t.id]) : '—'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="border-t-2 border-[#aebec8] flex w-full bg-white text-black font-bold px-2 py-1 shrink-0">
        <div className="w-[30px]" />
        <div className="w-[80px]" />
        <div className="w-[70px]" />
        <div className="w-[70px]" />
        <div className="flex-1 text-[11px] text-gray-500">{transactions?.length || 0} transaction(s)</div>
        <div className="w-[110px] text-right text-[#1e8585]">₹{fmt(transactions?.reduce((s, t) => s + t.debit, 0) || 0)}</div>
        <div className="w-[110px] text-right text-[#1e8585]">₹{fmt(transactions?.reduce((s, t) => s + t.credit, 0) || 0)}</div>
        <div className="w-[90px]" />
      </div>
    </div>
  );
};
